import { compact, isEmpty } from 'lodash';

import { CalendarIcon } from '@/components/icons/CalendarIcon';
import { MiniTableRow } from '@/components/tables/MiniTable/MiniTable';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { EntityDetail_EntityFragment } from '@/modules/entities/details/graphql/EntityDetailPage.generated';
import {
  getGRATTrustSummaryProperties,
  SUBSTITUTION_THRESHOLD_LABEL,
} from '@/modules/entities/details/grat/GRATTrustDetails';
import { getCLTTrustSummaryProperties } from '@/modules/entities/details/trusts/CLTTrustDetails';
import { getCRTTrustSummaryProperties } from '@/modules/entities/details/trusts/CRTTrustDetails';
import { getILITTrustSummaryProperties } from '@/modules/entities/details/trusts/ILITTrustDetails';
import { getQPRTTrustSummaryProperties } from '@/modules/entities/details/trusts/QPRTTrustDetails';
import { getRevocableIrrevocableTrustSummaryProperties } from '@/modules/entities/details/trusts/RevocableIrrevocableTrustDetails';
import { getSLATTrustSummaryProperties } from '@/modules/entities/details/trusts/SLATTrustDetails';
import { ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { ORDERED_TRUSTEE_LEVELS } from '@/modules/trustees/trustees.constants';
import { TrustAdvisorsList_TrustAdvisorFragment } from '@/modules/trusts/TrustAdvisorsList/graphql/TrustAdvisorsList.fragments.generated';
import { TrustSummaryData } from '@/modules/trusts/TrustDetails/TrustDetails.types';
import { TrustTrusteesList_TrusteeFragment } from '@/modules/trusts/TrustTrusteesList/graphql/TrustTrusteesList.fragments.generated';
import { EntityKind, TrustAdvisorLevel, TrusteeLevel } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { UnreachableError } from '@/utils/errors';

import {
  EntityCoverSlideData,
  EntityCoverSlideDataPoint,
  EntityCoverSlideDetailsList,
} from './EntityCoverSlide.types';
import {
  dataPointGetters,
  getTotalDeathBenefit,
  getTrimmedListForEntityCoverSlide,
} from './EntityCoverSlide.utils';

export function getTrustDetails(
  entity: EntityDetail_EntityFragment,
  entityKind:
    | EntityKind.IrrevocableTrust
    | EntityKind.RevocableTrust
    | EntityKind.SlatTrust
    | EntityKind.IlitTrust
): Omit<EntityCoverSlideData, 'title'> {
  let summary: TrustSummaryData | null = null;

  switch (entityKind) {
    case EntityKind.IrrevocableTrust:
    case EntityKind.RevocableTrust:
      summary = getRevocableIrrevocableTrustSummaryProperties({
        entity,
        entityType: getEntityTypeFromEntityKind(entityKind),
      });
      break;

    case EntityKind.SlatTrust:
      summary = getSLATTrustSummaryProperties({
        entity,
        entityType: ENTITY_TYPES.SLAT,
      });
      break;

    case EntityKind.IlitTrust:
      summary = getILITTrustSummaryProperties({
        entity,
        entityType: ENTITY_TYPES.ILIT,
      });
      break;

    default: {
      throw new UnreachableError({
        case: entityKind,
        message: `Unhandled trust subtype: ${entity.subtype.__typename}`,
      });
    }
  }

  if (!summary) {
    return {
      dataPoints: [],
      detailsLists: [],
    };
  }

  const dataPoints: EntityCoverSlideDataPoint[] = compact([
    dataPointGetters.getTotalMarketValueDataPoint(entity),
    dataPointGetters.getAssetLocationDataPoint(summary),
    dataPointGetters.getJurisdictionDataPoint(summary),
    dataPointGetters.getNamesDataPoint(summary),
    dataPointGetters.getTaxStatusDataPoint(summary),
    dataPointGetters.getEffectiveDateDataPoint(summary),
  ]);

  const detailsLists: EntityCoverSlideDetailsList[] = compact([
    getTrusteesList(summary),
    getTrustAdvisors(summary),
  ]);

  return {
    dataPoints,
    detailsLists,
    totalDeathBenefit: getTotalDeathBenefit(summary),
  };
}

export function getTimeBoundTrustDetails(
  entity: EntityDetail_EntityFragment,
  entityKind:
    | EntityKind.CrtTrust
    | EntityKind.CltTrust
    | EntityKind.QprtTrust
    | EntityKind.GratTrust
): Omit<EntityCoverSlideData, 'title'> {
  let summary: TrustSummaryData | null = null;
  let overrideLabel: 'grantors' | 'owners' | 'donors' | undefined = undefined;

  switch (entityKind) {
    case EntityKind.QprtTrust:
      summary = getQPRTTrustSummaryProperties({
        entity,
        entityType: ENTITY_TYPES.QPRT,
      });
      break;

    case EntityKind.CrtTrust:
      summary = getCRTTrustSummaryProperties({
        entity,
        entityType: ENTITY_TYPES.CRT,
      });
      overrideLabel = 'donors';
      break;

    case EntityKind.CltTrust:
      summary = getCLTTrustSummaryProperties({
        entity,
        entityType: ENTITY_TYPES.CLT,
      });
      overrideLabel = 'donors';
      break;

    case EntityKind.GratTrust:
      summary = getGRATTrustSummaryProperties({
        entity,
        entityType: ENTITY_TYPES.GRAT,
      });
      break;

    default: {
      throw new UnreachableError({
        case: entityKind,
        message: `Unhandled trust subtype: ${entity.subtype.__typename}`,
      });
    }
  }

  if (!summary) {
    return {
      dataPoints: [],
      detailsLists: [],
    };
  }

  const dataPoints: EntityCoverSlideDataPoint[] = compact([
    dataPointGetters.getTotalMarketValueDataPoint(entity),
    dataPointGetters.getAssetLocationDataPoint(summary),
    dataPointGetters.getJurisdictionDataPoint(summary),
    dataPointGetters.getNamesDataPoint(summary, overrideLabel),
    dataPointGetters.getTaxStatusDataPoint(summary),
    getTermDataPoint(summary),
  ]);
  const detailsLists: EntityCoverSlideDetailsList[] = compact([
    getTermDetailsList(summary, true),
    getTrusteesList(summary),
    getTrustAdvisors(summary),
  ]);

  return {
    dataPoints,
    detailsLists,
    totalDeathBenefit: getTotalDeathBenefit(summary),
  };
}

function getTermDataPoint({
  entityTerm,
}: {
  entityTerm?: {
    entityTermYears?: number | null | undefined;
    entityTermLifetime?: boolean | null | undefined;
  };
}): EntityCoverSlideDataPoint | null {
  if (!entityTerm?.entityTermYears && !entityTerm?.entityTermLifetime) {
    return null;
  }

  let value = `${entityTerm.entityTermYears} years`;
  if (entityTerm.entityTermLifetime) {
    value = 'Lifetime';
  }

  return {
    label: 'Term length',
    value,
    Icon: CalendarIcon,
  };
}

const TRUSTEES_LIST_TITLE: Record<string, string> = {
  primary: 'Primary',
  [TrusteeLevel.Primary]: 'First successor',
  [TrusteeLevel.Secondary]: 'Second successor',
  [TrusteeLevel.Tertiary]: 'Third successor',
  [TrusteeLevel.Quaternary]: 'Fourth successor',
  [TrusteeLevel.Quinary]: 'Fifth successor',
  [TrusteeLevel.Other]: 'Other successor',
};

export function getTrusteesList({
  trustees,
  successorTrustees,
}: {
  trustees: TrustTrusteesList_TrusteeFragment[];
  successorTrustees: TrustTrusteesList_TrusteeFragment[];
}): EntityCoverSlideDetailsList | null {
  if (!trustees) return null;

  const primaryRow: MiniTableRow | null = isEmpty(trustees)
    ? null
    : {
        value: compact(
          trustees.map(
            (trustee) =>
              trustee.individual?.displayName ||
              trustee.organization?.name ||
              ''
          )
        ).join(', '),
        label: TRUSTEES_LIST_TITLE.primary,
        valueProps: {
          sx: {
            textAlign: 'right',
          },
        },
      };

  const successorRows = successorTrustees.reduce<
    {
      level: TrusteeLevel;
      memberNames: string[];
    }[]
  >((acc, trustee) => {
    const level: TrusteeLevel = trustee.level || TrusteeLevel.Other;

    const relevantIndex = acc.findIndex((row) => row.level === level);

    if (relevantIndex !== -1) {
      acc[relevantIndex]!.memberNames.push(
        trustee.individual?.displayName || trustee.organization?.name || ''
      );
    } else {
      acc.push({
        level,
        memberNames: [
          trustee.individual?.displayName || trustee.organization?.name || '',
        ],
      });
    }

    return acc;
  }, []);
  const entries: MiniTableRow[] = compact([
    primaryRow,
    ...successorRows
      .sort((a, b) => {
        const aIndex = ORDERED_TRUSTEE_LEVELS.indexOf(a.level);
        const bIndex = ORDERED_TRUSTEE_LEVELS.indexOf(b.level);
        return aIndex - bIndex;
      })
      .map<MiniTableRow>(({ level, memberNames }) => ({
        value: compact(memberNames).join(', '),
        label: TRUSTEES_LIST_TITLE[level],
        valueProps: {
          sx: {
            textAlign: 'right',
          },
        },
      })),
  ]);

  return {
    title: 'Trustees',
    entries: getTrimmedListForEntityCoverSlide(entries),
  };
}

const TRUST_ADVISORS_LIST_TITLE: Record<TrustAdvisorLevel, string> = {
  [TrustAdvisorLevel.Primary]: 'Primary',
  [TrustAdvisorLevel.Secondary]: 'Secondary',
  [TrustAdvisorLevel.Tertiary]: 'Tertiary',
  [TrustAdvisorLevel.Other]: 'Other',
};
function compareAdvisorNames(
  a: TrustAdvisorsList_TrustAdvisorFragment,
  b: TrustAdvisorsList_TrustAdvisorFragment
) {
  const aName = a.individual?.lastName || a.organization?.name || '';
  const bName = b.individual?.lastName || b.organization?.name || '';

  return aName.localeCompare(bName);
}

function getTrustAdvisors({
  trustAdvisors,
}: {
  trustAdvisors?: TrustAdvisorsList_TrustAdvisorFragment[];
}): EntityCoverSlideDetailsList | null {
  if (!trustAdvisors) return null;

  const {
    primaryTrustAdvisors,
    secondaryTrustAdvisors,
    tertiaryTrustAdvisors,
    otherTrustAdvisors,
  } = trustAdvisors.reduce<{
    primaryTrustAdvisors: TrustAdvisorsList_TrustAdvisorFragment[];
    secondaryTrustAdvisors: TrustAdvisorsList_TrustAdvisorFragment[];
    tertiaryTrustAdvisors: TrustAdvisorsList_TrustAdvisorFragment[];
    otherTrustAdvisors: TrustAdvisorsList_TrustAdvisorFragment[];
  }>(
    (acc, trustAdvisor) => {
      if (trustAdvisor.level === TrustAdvisorLevel.Primary) {
        acc.primaryTrustAdvisors.push(trustAdvisor);
      } else if (trustAdvisor.level === TrustAdvisorLevel.Secondary) {
        acc.secondaryTrustAdvisors.push(trustAdvisor);
      } else if (trustAdvisor.level === TrustAdvisorLevel.Tertiary) {
        acc.tertiaryTrustAdvisors.push(trustAdvisor);
      } else if (trustAdvisor.level === TrustAdvisorLevel.Other) {
        acc.otherTrustAdvisors.push(trustAdvisor);
      } else {
        if (!trustAdvisor.level) {
          diagnostics.error(
            `Trust advisor ${trustAdvisor.id} [${trustAdvisor.individual?.displayName || trustAdvisor.organization?.name}] has an unknown level: ${trustAdvisor.level}`
          );
        }
      }
      return acc;
    },
    {
      primaryTrustAdvisors: [],
      secondaryTrustAdvisors: [],
      tertiaryTrustAdvisors: [],
      otherTrustAdvisors: [],
    }
  );

  const entries: MiniTableRow[] = compact([
    !isEmpty(primaryTrustAdvisors) && {
      label: TRUST_ADVISORS_LIST_TITLE[TrustAdvisorLevel.Primary],
      value: compact(
        primaryTrustAdvisors
          .sort(compareAdvisorNames)
          .map(
            (trustAdvisor) =>
              trustAdvisor.individual?.displayName ||
              trustAdvisor.organization?.name ||
              ''
          )
      ).join(', '),
    },
    !isEmpty(secondaryTrustAdvisors) && {
      label: TRUST_ADVISORS_LIST_TITLE[TrustAdvisorLevel.Secondary],
      value: compact(
        secondaryTrustAdvisors
          .sort(compareAdvisorNames)
          .map(
            (trustAdvisor) =>
              trustAdvisor.individual?.displayName ||
              trustAdvisor.organization?.name ||
              ''
          )
      ).join(', '),
    },
    !isEmpty(tertiaryTrustAdvisors) && {
      label: TRUST_ADVISORS_LIST_TITLE[TrustAdvisorLevel.Tertiary],
      value: compact(
        tertiaryTrustAdvisors
          .sort(compareAdvisorNames)
          .map(
            (trustAdvisor) =>
              trustAdvisor.individual?.displayName ||
              trustAdvisor.organization?.name ||
              ''
          )
      ).join(', '),
    },
    !isEmpty(otherTrustAdvisors) && {
      label: TRUST_ADVISORS_LIST_TITLE[TrustAdvisorLevel.Other],
      value: compact(
        otherTrustAdvisors
          .sort(compareAdvisorNames)
          .map(
            (trustAdvisor) =>
              trustAdvisor.individual?.displayName ||
              trustAdvisor.organization?.name ||
              ''
          )
      ).join(', '),
    },
  ]);

  return {
    title: 'Trust advisors',
    entries: getTrimmedListForEntityCoverSlide(entries),
  };
}

const EXCLUDED_LABELS = [SUBSTITUTION_THRESHOLD_LABEL];
const EXCLUDED_LABELS_TIME_BOUND = ['Term length'];
function getTermDetailsList(
  summary: TrustSummaryData,
  isTimeBound?: boolean
): EntityCoverSlideDetailsList {
  const labelsToExclude = isTimeBound
    ? [...EXCLUDED_LABELS, ...EXCLUDED_LABELS_TIME_BOUND]
    : EXCLUDED_LABELS;
  const entries = summary.trustDetailsItems
    .filter((item) => {
      if (labelsToExclude.includes(item.label ?? '')) {
        return false;
      }
      return !!item.value && item.value !== EMPTY_CONTENT_HYPHEN;
    })
    .map<MiniTableRow>((item) => ({
      label: item.label,
      value: item.value,
    }));

  return {
    title: 'Term details',
    entries,
  };
}
